<template>
  <div class="map-widget">
    <div class="widget-header">
      <div class="header-title">
        <h2>{{ title }}</h2>
        <p>{{ subtitle }}</p>
      </div>
      <div class="controls-row">
        <button class="toggle-btn" @click="viewMode = viewMode === 'map' ? 'table' : 'map'">
          {{ viewMode === 'map' ? t('viewTable', lang) : t('viewMap', lang) }}
        </button>
        <button v-if="viewMode === 'table'" class="export-btn" @click="exportToExcel">
          {{ t('exportXlsx', lang) }}
        </button>
      </div>
    </div>

    <div class="map-container">
      <template v-if="viewMode === 'map'">
        <div
          v-if="hoveredFeature"
          class="tooltip"
          :style="{ top: tooltipPos.y + 'px', left: tooltipPos.x + 'px' }"
        >
          <strong>{{ hoveredFeature.properties.NOMCOMAR }}</strong>
          <br />
          Vehicles: {{ getVehicleCount(hoveredFeature).toLocaleString('es-ES') }}
        </div>

        <svg :viewBox="`0 0 ${width} ${height}`" class="catalonia-map" @mousemove="updateTooltipPos">
          <g>
            <path
              v-for="(item, index) in featuresWithColors"
              :key="index"
              :d="item.path"
              :fill="item.color"
              class="comarca-path"
              @mouseenter="hoveredFeature = item.feat"
              @mouseleave="hoveredFeature = null"
            ></path>
          </g>
        </svg>
      </template>

      <div v-else class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th>Comarca</th>
              <th>Vehicles</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in tableData" :key="index">
              <td>{{ row.Comarca }}</td>
              <td>{{ row.Vehicles !== null && row.Vehicles !== undefined ? row.Vehicles.toLocaleString('es-ES') : 'N/A' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, shallowRef, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { t } from '../i18n/index.js';
import { debugLog, debugWarn } from '../utils/debug.js';
import { feature } from 'topojson-client';
import { geoMercator, geoPath } from 'd3-geo';
import * as XLSX from 'xlsx';
import topoData from '../data/LIMADM_COMARCA_SIMP.json';

const props = defineProps({
  data: { type: Array, default: () => [] },
  mapKeyField: { type: String, default: 'NOMCOMAR' },
  dataKeyField: { type: String, default: 'comarca' },
  valueField: { type: String, default: 'vehicles' },
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  lang: { type: String, default: 'ca_es' }
});

// shallowRef per evitar reactivitat profunda sobre el GeoJSON (objecte gran).
const features = shallowRef([]);
const pathGenerator = shallowRef(null);
const width = ref(800);
const height = ref(600);
const hoveredFeature = shallowRef(null);
const tooltipPos = ref({ x: 0, y: 0 });
const vehiclesData = shallowRef({});
const vehiclesDataNorm = shallowRef({});
const minVehicles = ref(0);
const maxVehicles = ref(1);
const viewMode = ref('map');

const normalizeKey = (s) => String(s ?? '')
  .toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9]/g, '')
  .trim();

const tableData = computed(() => {
  return Object.entries(vehiclesData.value)
    .map(([comarca, vehicles]) => ({
      Comarca: comarca,
      Vehicles: vehicles
    }))
    .sort((a, b) => a.Comarca.localeCompare(b.Comarca));
});

// Paths cachejats: només es recalculen quan canvia el conjunt de features
// o el pathGenerator, no quan canvien les dades de vehicles ni l'hover.
const featurePaths = computed(() => {
  const pg = pathGenerator.value;
  const feats = features.value;
  if (!pg || feats.length === 0) return [];
  return feats.map(feat => pg(feat));
});

const featuresWithColors = computed(() => {
  const feats = features.value;
  const paths = featurePaths.value;
  if (paths.length === 0) return [];
  return feats.map((feat, i) => ({
    feat,
    path: paths[i],
    color: computeColor(feat)
  }));
});

function lookupCount(featName) {
  if (featName == null) return 0;
  const direct = vehiclesData.value[featName];
  if (direct !== undefined) return direct;
  const norm = vehiclesDataNorm.value[normalizeKey(featName)];
  return norm !== undefined ? norm : 0;
}

function computeColor(feat) {
  const count = lookupCount(feat.properties[props.mapKeyField]);
  if (!count) return '#e5e7eb';

  const ratio = (count - minVehicles.value) / (maxVehicles.value - minVehicles.value);
  const opacity = 0.2 + (ratio * 0.8);
  return `rgba(37, 99, 235, ${opacity})`;
}

onMounted(() => {
  initMap();
});

function ingestData() {
  if (!props.data || props.data.length === 0) {
    vehiclesData.value = {};
    vehiclesDataNorm.value = {};
    return;
  }
  const direct = {};
  const norm = {};
  props.data.forEach(item => {
    const key = item[props.dataKeyField];
    const value = item[props.valueField];
    if (key && value !== undefined && value !== null) {
      direct[key] = value;
      norm[normalizeKey(key)] = value;
    }
  });
  vehiclesData.value = direct;
  vehiclesDataNorm.value = norm;
  const counts = Object.values(direct).filter(v => typeof v === 'number');
  minVehicles.value = 0;
  maxVehicles.value = counts.length ? Math.max(...counts) : 1;

  // Diagnòstic: comparar claus rebudes amb les del TopoJSON
  const apiKeys = Object.keys(direct);
  const topoNames = features.value.map(f => f.properties?.[props.mapKeyField]).filter(Boolean);
  const matched = apiKeys.filter(k => lookupCount(k) > 0 || topoNames.some(n => normalizeKey(n) === normalizeKey(k)));
  debugLog('[MapWidget] dataKeyField:', props.dataKeyField, '| valueField:', props.valueField);
  debugLog('[MapWidget] sample API keys:', apiKeys.slice(0, 5));
  debugLog('[MapWidget] sample TopoJSON names:', topoNames.slice(0, 5));
  debugLog('[MapWidget] matched:', matched.length, '/', apiKeys.length);
  if (matched.length === 0 && apiKeys.length > 0) {
    debugWarn('[MapWidget] CAP comarca matchejada — primer item rebut:', props.data[0]);
  }
}

watch(() => props.data, ingestData);

function initMap() {
  const objectKey = Object.keys(topoData.objects)[0];
  const geoJson = feature(topoData, topoData.objects[objectKey]);

  const projection = geoMercator().fitSize([width.value, height.value], geoJson);
  pathGenerator.value = geoPath().projection(projection);
  // Object.freeze evita que Vue intenti reactivar res en aquest array gran.
  features.value = Object.freeze(geoJson.features);

  ingestData();
}

function getVehicleCount(feat) {
  return lookupCount(feat.properties[props.mapKeyField]);
}

// Throttle amb requestAnimationFrame: una sola actualització per frame
// enlloc d'una per cada mousemove (que dispara re-renders).
let rafId = null;
function updateTooltipPos(event) {
  if (rafId) return;
  const x = event.clientX + 15;
  const y = event.clientY + 15;
  rafId = requestAnimationFrame(() => {
    tooltipPos.value = { x, y };
    rafId = null;
  });
}

onBeforeUnmount(() => {
  if (rafId) cancelAnimationFrame(rafId);
});

function exportToExcel() {
  const ws = XLSX.utils.json_to_sheet(tableData.value);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Vehicles");
  XLSX.writeFile(wb, "mapa_vehicles_comarca.xlsx");
}
</script>

<style scoped>
.map-widget {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  position: relative;
  overflow-x: hidden;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.map-widget .map-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.widget-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.widget-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin-top: 0;
  margin-bottom: 4px;
}

.widget-header p {
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0;
  margin-bottom: 16px;
}

.map-container {
  position: relative;
  width: 100%;
}

.catalonia-map {
  width: 100%;
  height: auto;
  max-height: 500px;
  display: block;
}

.comarca-path {
  stroke: #ffffff;
  stroke-width: 1px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.comarca-path:hover {
  stroke: #1f2937;
  stroke-width: 2px;
  fill: #f59e0b !important;
}

.tooltip {
  position: fixed;
  background-color: rgba(17, 24, 39, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.875rem;
  pointer-events: none;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.controls-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.toggle-btn,
.export-btn {
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background-color: #f9fafb;
  font-size: 0.875rem;
  color: #374151;
  outline: none;
  cursor: pointer;
  transition: all 0.2s;
}

.toggle-btn:hover {
  background-color: #f3f4f6;
  border-color: #d1d5db;
}

.export-btn {
  background-color: #10b981;
  color: white;
  border-color: #059669;
}

.export-btn:hover {
  background-color: #059669;
  border-color: #047857;
}

.table-container {
  max-height: 480px;
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: 10px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.data-table th,
.data-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.data-table th {
  background-color: #f9fafb;
  font-weight: 600;
  color: #374151;
  position: sticky;
  top: 0;
}

.data-table tr:hover {
  background-color: #f9fafb;
}
</style>
